import { 
    Button, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    Image, Input, useDisclosure, Modal, ModalOverlay,
    ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Switch, Flex, Icon, Text, Box, Badge, Tooltip, useColorModeValue,
    Alert, AlertIcon, Center, Skeleton
} from '@chakra-ui/react';
import { 
    FaCheckCircle, FaExclamationCircle, FaSearch, FaSort, FaSortUp, FaSortDown, FaGift,
    FaIdCard,       
    FaTag,          
    FaImage,        
    FaHeading,      
    FaAlignLeft,    
    FaToggleOn,     
    FaCoins,        
    FaCalendarAlt,
} from 'react-icons/fa';
import React, { useEffect, useState, useCallback } from "react";
import { useGetAllRewardsDataMutation, useUpdateRewardStatusMutation } from "../../slices/adminApiSlice";
import Loader from "../Loader";
import { ToastService } from '../../services/toast/toastService';
import AdminAddReward from './AdminAddReward';

const AdminRewardTable = () => {
    const { success, error } = ToastService();
    const [rewardsData, setRewardsData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortColumn, setSortColumn] = useState('title');
    const [sortOrder, setSortOrder] = useState('asc');
    const [getRewardsData, { isLoading, error: fetchError }] = useGetAllRewardsDataMutation();
    const [updateRewardStatus, { isUpdateLoading }] = useUpdateRewardStatusMutation();
    const [currentReward, setCurrentReward] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [rewardId, setRewardId] = useState(null);
    const [rewardIsActive, setRewardIsActive] = useState(false);

    // Color scheme
    const colorScheme = 'teal';
    const headerBgColor = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.600`);
    const hoverColor = useColorModeValue(`${colorScheme}.50`, 'gray.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const inputBg = useColorModeValue('white', 'gray.700');
    const tblThBgColor = useColorModeValue(`${colorScheme}.600`, `${colorScheme}.800`);
    const activeColor = useColorModeValue('green.500', 'green.300');
    const inactiveColor = useColorModeValue('red.500', 'red.300');
    // const headerColor = useColorModeValue('gray.700', 'white');

    const fetchRewards = useCallback(async () => {
        try {
            const response = await getRewardsData().unwrap(); // Use unwrap to handle errors cleanly
            setRewardsData(response);
            } catch (error) {
            // console.error('Error fetching all rewards:', error);
            }
        }, [getRewardsData]); // Empty dependency array

        useEffect(() => {
        fetchRewards();
    }, [ fetchRewards ]);

    const handleToggle = (id, event) => {
        const isActive = event.target.checked;
        setRewardId(id);
        setRewardIsActive(isActive);
        setIsConfirmationModalOpen(true);
    };

    const handleRewardConfirmation = async () => {
        setIsConfirmationModalOpen(false);
        try {
            if (rewardId) {
                await updateRewardStatus({ id: rewardId, isActive: rewardIsActive }).unwrap();
                success(`Reward ${rewardIsActive ? 'activated' : 'deactivated'} successfully!`);
                fetchRewards();
            }
        } catch (err) {
            error(err.data?.message || 'Failed to update reward status');
        } finally {
            setRewardId(null);
        }
    };

    // Filter and sort rewards
    const filteredRewards = Array.isArray(rewardsData) 
    ? rewardsData.filter(reward => 
        reward?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        reward?.description?.toLowerCase().includes(searchQuery.toLowerCase())
    ) 
    : [];

    const sortedRewards = [...filteredRewards].sort((a, b) => {
        const aValue = a[sortColumn]?.toString().toLowerCase() || '';
        const bValue = b[sortColumn]?.toString().toLowerCase() || '';
        return aValue < bValue ? (sortOrder === 'asc' ? -1 : 1) : aValue > bValue ? (sortOrder === 'asc' ? 1 : -1) : 0;
    });

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortOrder('asc');
        }
    };

    // Column configuration
    const columns = [
        { 
          key: '_id', 
          label: 'Reward ID', 
          width: '10%',
          icon: FaIdCard,
          colorScheme: 'purple',
          tooltip: 'Sort by Reward ID'
        },
        { 
          key: 'type', 
          label: 'Type', 
          width: '10%',
          icon: FaTag,
          colorScheme: 'blue',
          tooltip: 'Sort by Type'
        },
        { 
          key: 'image', 
          label: 'Image', 
          width: '15%',
          icon: FaImage,
          colorScheme: 'teal'
        },
        { 
          key: 'title', 
          label: 'Title', 
          width: '15%',
          icon: FaHeading,
          colorScheme: 'green',
          tooltip: 'Sort by Title'
        },
        { 
          key: 'description', 
          label: 'Description', 
          width: '35%',
          icon: FaAlignLeft,
          colorScheme: 'orange',
          tooltip: 'Sort by Description'
        },
        { 
          key: 'is_active', 
          label: 'Status', 
          width: '5%',
          icon: FaToggleOn,
          colorScheme: 'yellow'
        },
        { 
          key: 'points_required', 
          label: 'Points', 
          width: '5%',
          icon: FaCoins,
          colorScheme: 'cyan',
          tooltip: 'Sort by Points'
        },
        { 
          key: 'months_to_expire', 
          label: 'Expiry', 
          width: '5%',
          icon: FaCalendarAlt,
          colorScheme: 'red',
          tooltip: 'Sort by Expiry'
        }
      ];

    return (
        <Box p={4} maxW="100vw" overflowX="auto">
            {/* <Heading size="lg" mb={6} color={headerColor}>
                Reward Management
            </Heading> */}

            {/* Action Bar */}
            <Flex justify="space-between" mb={6} direction={{ base: 'column', md: 'row' }} gap={4}>
                <Box position="relative" flex="1">
                    <Input
                        placeholder="Search rewards..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="md"
                        borderRadius="lg"
                        bg={inputBg}
                        pl={10}
                        _focus={{
                            borderColor: `${colorScheme}.500`,
                            boxShadow: `0 0 0 1px ${useColorModeValue(`${colorScheme}.300`, `${colorScheme}.600`)}`
                        }}
                    />
                    <Icon
                        as={FaSearch}
                        position="absolute"
                        left={3}
                        top="50%"
                        transform="translateY(-50%)"
                        color="gray.400"
                    />
                </Box>
                
                <Button
                    colorScheme={colorScheme}
                    variant="outline"
                    onClick={() => {
                        setCurrentReward(null);
                        onOpen();
                    }}
                    minW="150px"
                >
                    Create Reward
                </Button>
            </Flex>

            <AdminAddReward
                selectedReward={currentReward}
                isOpen={isOpen}
                onClose={onClose}
                fetchRewards={fetchRewards}
            />

            {/* Error Handling */}
            {fetchError && (
                <Alert status="error" borderRadius="lg" mb={6}>
                    <AlertIcon />
                    Error fetching rewards data: {fetchError.message}
                </Alert>
            )}

            {/* Table Container */}
            <TableContainer
                borderRadius="lg"
                borderWidth="1px"
                borderColor={borderColor}
                bg={inputBg}
                boxShadow="sm"
                >
                {isLoading ? (
                    <Center minH="300px"><Loader color={inputBg} minH='50vh' /></Center>
                ) : sortedRewards.length > 0 ? (
                    <Table variant="striped" size={'sm'} colorScheme={`${colorScheme}Alpha`}>
                        <Thead bg={headerBgColor}>
                        <Tr>
                            {columns.map((column) => (
                            <Th
                                key={column.key}
                                color="white"
                                onClick={() => column.key !== 'image' && handleSort(column.key)}
                                cursor={column.key !== 'image' && column.key !== 'is_active' ? 'pointer' : 'default'}
                                _hover={{ 
                                    bg: column.key !== 'image' && column.key !== 'is_active' ? tblThBgColor : undefined,
                                    transform: 'translateY(-2px)'
                                }}
                                transition="all 0.2s"
                                py={4}
                                >
                                <Tooltip 
                                    label={column.tooltip}
                                    hasArrow 
                                    placement="top"
                                    bg={`${column.colorScheme}.500`}
                                >
                                <Flex align="center" gap={2}>
                                    <Center
                                        w={8}
                                        h={8}
                                        borderRadius="md"
                                        bg={`${column.colorScheme}.100`}
                                        color={`${column.colorScheme}.600`}
                                    >
                                    <Icon as={column.icon} boxSize={4} />
                                    </Center>
                                    <Text fontWeight="semibold">{column.label}</Text>
                                    <Icon
                                    as={
                                        sortColumn === column.key
                                        ? sortOrder === 'asc'
                                            ? FaSortUp
                                            : FaSortDown
                                        : FaSort
                                    }
                                    opacity={sortColumn === column.key ? 1 : 0.3}
                                    ml={1}
                                    />
                                </Flex>
                                </Tooltip>
                            </Th>
                            ))}
                        </Tr>
                        </Thead>
                        <Tbody>
                            {sortedRewards.map((reward) => (
                                <Tr
                                    key={reward._id}
                                    _hover={{ bg: hoverColor }}
                                    transition="background-color 0.2s"
                                >
                                    <Td
                                        onClick={() => {
                                            setCurrentReward(reward);
                                            onOpen();
                                        }}
                                        cursor="pointer"
                                        fontWeight="medium"
                                    >
                                        <Tooltip label={reward._id}>
                                            <Text isTruncated>{reward._id}</Text>
                                        </Tooltip>
                                    </Td>
                                    <Td>
                                        <Badge
                                            colorScheme={reward.type === 'points' ? 'blue' : 'orange'}
                                            variant="subtle"
                                            borderRadius="full"
                                            px={3}
                                            py={1}
                                        >
                                            {reward.type}
                                        </Badge>
                                    </Td>
                                    <Td>
                                        <Skeleton isLoaded={!!reward?.image_url} borderRadius="md">
                                            <Image
                                            src={reward?.image_url || '/default_reward.png'}
                                            alt={reward.title}
                                            width="100%"
                                            height="100%"
                                            objectFit="cover"
                                            borderRadius="md"
                                            fallback={
                                                <Center w="100%" h="100%" bg="gray.100">
                                                <Icon as={FaGift} boxSize="24px" color="gray.400" />
                                                </Center>
                                            }
                                            />
                                        </Skeleton>
                                    </Td>
                                    <Td fontWeight="semibold">{reward.title}</Td>
                                    <Td>
                                        <Tooltip label={reward.description}>
                                            <Text
                                                isTruncated
                                                maxW="300px"
                                                whiteSpace="nowrap"
                                                overflow="hidden"
                                                textOverflow="ellipsis"
                                                >
                                                {reward.description}
                                            </Text>
                                        </Tooltip>
                                    </Td>
                                    <Td>
                                        <Switch
                                            isChecked={reward.is_active}
                                            onChange={(e) => handleToggle(reward._id, e)}
                                            colorScheme={colorScheme}
                                            size="md"
                                        />
                                    </Td>
                                    <Td fontWeight="bold">
                                        {reward.points_required}
                                    </Td>
                                    <Td>
                                        <Badge
                                            colorScheme={reward.months_to_expire > 3 ? 'green' : 'orange'}
                                            variant="subtle"
                                            >
                                            {reward.months_to_expire}m
                                        </Badge>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                ) : (
                    <Center minH="200px">
                        <Alert status="info" borderRadius="md" maxW="500px">
                            <AlertIcon />
                            {searchQuery ? 'No rewards match your search criteria' : 'No rewards found'}
                        </Alert>
                    </Center>
                )}
            </TableContainer>

            {/* Confirmation Modal */}
            <Modal isOpen={isConfirmationModalOpen} onClose={() => setIsConfirmationModalOpen(false)}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Flex align="center">
                            <Icon
                                as={rewardIsActive ? FaCheckCircle : FaExclamationCircle}
                                color={rewardIsActive ? activeColor : inactiveColor}
                                boxSize={6}
                                mr={2}
                            />
                            {rewardIsActive ? 'Activate Reward' : 'Deactivate Reward'}
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="lg" fontWeight="bold" mb={2}>
                            {rewardIsActive
                                ? 'Are you sure you want to activate this reward?'
                                : 'Are you sure you want to deactivate this reward?'}
                        </Text>
                        <Text fontSize="md" color="gray.600" mb={4}>
                            {rewardIsActive
                                ? 'This reward will become available for redemption.'
                                : 'Users will no longer be able to redeem this reward.'}
                        </Text>
                    </ModalBody>

                    <ModalFooter>
                        <Button
                            isLoading={isUpdateLoading}
                            colorScheme={rewardIsActive ? 'green' : 'red'}
                            onClick={handleRewardConfirmation}
                            mr={3}
                            >
                            {rewardIsActive ? 'Activate' : 'Deactivate'}
                        </Button>
                        <Button variant="outline" onClick={() => setIsConfirmationModalOpen(false)}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default AdminRewardTable;
